import { Component, OnInit, OnDestroy, ElementRef, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { takeUntil, takeWhile } from 'rxjs/operators';
import { Subject, timer, interval } from 'rxjs';
import { MatDialog } from '@angular/material/dialog';

import { AppService } from '../app.service';
import {
  ADMIN_CONSTANT,
  ResponseBody,
  AdminConfig,
  OrgMap,
} from '../app.model';
import { BridgeDialogComponent } from '../shared/components/bridge-dialog/bridge-dialog.component';

@Component({
  selector: 'app-authentication',
  templateUrl: './authentication.component.html',
  styleUrls: ['./authentication.component.scss'],
})
export class AuthenticationComponent implements OnInit, OnDestroy {
  @ViewChild('inputOtpField') inputOtpField: ElementRef<HTMLInputElement>;

  action: string;
  isInitial: boolean;
  resendOTP: boolean;
  resendOTPTimer: number;
  authenticationForm: FormGroup;

  destroy: Subject<any> = new Subject<any>();

  get inputOTP() {
    return this.authenticationForm.get('InputOTP');
  }

  constructor(
    private router: Router,
    private fb: FormBuilder,
    private dialog: MatDialog,
    private service: AppService,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.initForm();
    this.service.setTitle('Authentication');
    this.action = 'LOGIN';
    this.isInitial = true;
  }

  ngOnDestroy(): void {
    this.destroy.next(null);
    this.destroy.complete();
  }

  haveTwoFactorAuthentication(): boolean {
    const _2FA = this.service
      .getToken(ADMIN_CONSTANT.ADMIN_TOKEN_ADMIN_CONFIG)
      .find(
        (f: AdminConfig) =>
          f.ConfigCode === ADMIN_CONSTANT.SITE_TWO_FACTOR_AUTHENTICATION
      );
    return !!_2FA && _2FA.ConfigValue === ADMIN_CONSTANT.STATUS_TRUE
      ? true
      : false;
  }

  initForm(): void {
    this.authenticationForm = this.fb.group({
      InputOTP: this.fb.control(''),
      Email: this.fb.control('', [
        Validators.required,
        Validators.pattern('^[a-z0-9._%+-]+@[a-z0-9.-]+\\.[a-z]{2,4}$'),
      ]),
      AdminPassword: this.fb.control('', [
        Validators.required,
        Validators.minLength(8),
      ]),
    });
  }

  onSubmit(): void {
    this.isInitial ? this.onGenerateOTP() : this.onAuthentication();
  }

  onGenerateOTP(): void {
    if (this.authenticationForm.valid) {
      this.service
        .onGlobalLogin(
          this.haveTwoFactorAuthentication(),
          this.authenticationForm.getRawValue()
        )
        .pipe(takeUntil(this.destroy))
        .subscribe(
          (response) => {
            if (!this.haveTwoFactorAuthentication()) {
              this.processAfterAuthentication(response);
            } else {
              if (response.Header.Status === ADMIN_CONSTANT.SUCCESS_STATUS) {
                this.isInitial = false;
                this.resendOTPTimerFn();
                this.action = 'VALIDATE OTP';
                this.setInputOTPValidation(true);
                this.authenticationForm.markAsPristine();
                this.authenticationForm.markAsUntouched();
                this.authenticationForm.disable({ emitEvent: false });
                this.inputOTP.enable({ emitEvent: false });
                this.focusInputOTPField();
                this.service.showToast(ADMIN_CONSTANT.OTP_DELIVERED_MESSAGE);
              } else if (
                response.Header.Status === ADMIN_CONSTANT.ERROR_STATUS
              ) {
                this.service.showToast(response.Error.ErrorMessage);
              }
            }
          },
          (err) => this.service.showToast(ADMIN_CONSTANT.SERVER_ERROR_MESSAGE)
        );
    } else {
      this.service.showToast('Please provide required details');
    }
  }

  onAuthentication(): void {
    this.service
      .onGlobalLoginValidateOTP(this.authenticationForm.getRawValue())
      .pipe(takeUntil(this.destroy))
      .subscribe(
        (response) => {
          this.processAfterAuthentication(response);
        },
        (err) => this.service.showToast(ADMIN_CONSTANT.SERVER_ERROR_MESSAGE)
      );
  }

  processAfterAuthentication(response: ResponseBody): void {
    if (response.Header.Status === ADMIN_CONSTANT.SUCCESS_STATUS) {
      this.service.setToken(
        ADMIN_CONSTANT.ADMIN_TOKEN_ADMIN_GLOBAL_USER_INFO,
        response.Body.BusinessData.UserData
      );
      // this.openBridgeDialog(
      //   this.authenticationForm.get('Email').value,
      //   !!response.Body.BusinessData.UserData.TempPasswordFlag,
      //   response.Body.BusinessData.OrgMap
      // );
      this.service.showToast(ADMIN_CONSTANT.OTP_VALIDATED);
      this.service.setToken(
        ADMIN_CONSTANT.ADMIN_TOKEN_USER_ID,
        response.Body.BusinessData.UserData.UsrId
      );
      if (this.route.snapshot.queryParamMap.has('redirectURL')) {
        this.router
          .navigateByUrl(
            this.route.snapshot.queryParamMap.get('redirectURL')
          )
          .catch(() =>
            this.router.navigate(['dashboard'], { replaceUrl: true })
          );
      } else {
        this.router.navigate(['dashboard'], { replaceUrl: true });
      }
    } else if (response.Header.Status === ADMIN_CONSTANT.ERROR_STATUS) {
      this.service.showToast(response.Error.ErrorMessage);
    }
  }

  setInputOTPValidation(set: boolean): void {
    if (set) {
      this.inputOTP.setValidators([
        Validators.required,
        Validators.maxLength(6),
        Validators.pattern('^\\d{6}$'),
      ]);
    } else {
      this.inputOTP.clearValidators();
    }
    this.inputOTP.updateValueAndValidity();
  }

  onResendOTP(): void {
    this.service
      .onGlobalLoginResendOTP(this.authenticationForm.getRawValue())
      .pipe(takeUntil(this.destroy))
      .subscribe(
        (response) => {
          if (response.Header.Status === ADMIN_CONSTANT.SUCCESS_STATUS) {
            this.service.showToast(ADMIN_CONSTANT.OTP_DELIVERED_MESSAGE);
            this.resendOTPTimerFn();
          } else if (response.Header.Status === ADMIN_CONSTANT.ERROR_STATUS) {
            this.service.showToast(response.Error.ErrorMessage);
          }
        },
        (err) => this.service.showToast(ADMIN_CONSTANT.SERVER_ERROR_MESSAGE)
      );
  }

  onChange(): void {
    this.resendOTPTimer = ADMIN_CONSTANT.RESEND_OTP_TIMER;
    this.authenticationForm.enable({ emitEvent: false });
    this.authenticationForm.reset({ emitEvent: false });
    this.setInputOTPValidation(false);
    this.resendOTP = true;
    this.action = 'LOGIN';
    this.isInitial = true;
  }

  resendOTPTimerFn(): void {
    this.resendOTP = false;
    this.resendOTPTimer = ADMIN_CONSTANT.RESEND_OTP_TIMER;
    timer(ADMIN_CONSTANT.TIMER_DURATION_ULTRA_HIGH)
      .pipe(takeWhile(() => !this.resendOTP))
      .subscribe(() => (this.resendOTP = true));
    interval(1000)
      .pipe(takeWhile(() => !this.resendOTP, true))
      .subscribe(
        () =>
          (this.resendOTPTimer = this.resendOTP
            ? ADMIN_CONSTANT.RESEND_OTP_TIMER
            : this.resendOTPTimer > 0
            ? --this.resendOTPTimer
            : 0)
      );
  }

  onForgotPassword(): void {
    this.openBridgeDialog(
      this.authenticationForm.get('Email').value,
      false,
      null,
      true
    );
  }

  focusInputOTPField(): void {
    setTimeout(() => this.inputOtpField?.nativeElement?.focus());
  }

  openBridgeDialog(
    email: string,
    resetPassword: boolean,
    orgMap: OrgMap,
    forgotPassword: boolean = false
  ): void {
    this.dialog
      .open(BridgeDialogComponent, {
        width: '325px',
        maxWidth: '325px',
        hasBackdrop: true,
        disableClose: true,
        position: { top: '15%' },
        data: { email, resetPassword, orgMap, forgotPassword },
      })
      .afterClosed()
      .pipe(takeUntil(this.destroy))
      .subscribe((d) => {
        if (!!d && !!d.OrgCode) {
          this.service.selectedOrganization = d;
          this.service.setToken(
            ADMIN_CONSTANT.ADMIN_TOKEN_ORG_ID,
            this.service.selectedOrganization.OrgId
          );
          this.service.setToken(
            ADMIN_CONSTANT.ADMIN_TOKEN_USER_ID,
            this.service.selectedOrganization.UsrId
          );
          this.service.setToken(
            ADMIN_CONSTANT.ADMIN_TOKEN_ORG_CODE,
            this.service.selectedOrganization.OrgCode
          );
          this.service.setToken(
            ADMIN_CONSTANT.ADMIN_TOKEN_ADMIN_SELECTED_ULINK,
            this.service.selectedOrganization.UniversalAppLink
          );
          this.service.setToken(
            ADMIN_CONSTANT.ADMIN_TOKEN_ADMIN_SUPER_USER_FLAG,
            this.service.selectedOrganization.SuperAdminFlag
          );
          this.service.setToken(
            ADMIN_CONSTANT.ADMIN_TOKEN_ADMIN_SELECTED_ORGANIZATION,
            this.service.selectedOrganization
          );

          if (this.route.snapshot.queryParamMap.has('redirectURL')) {
            this.router
              .navigateByUrl(
                this.route.snapshot.queryParamMap.get('redirectURL')
              )
              .catch(() =>
                this.router.navigate(['dashboard'], { replaceUrl: true })
              );
          } else {
            this.router.navigate(['dashboard'], { replaceUrl: true });
          }
        }
      });
  }
}
